/**
 * intuitionCoordinator.js — FASE R7 coordinator.
 *
 * Combines all R7 modules into a compact intuition context string.
 * Injected AFTER refinement, BEFORE humanRhythm (R6) in the system prompt.
 *
 * Purpose:
 *   - Collapse signal noise to dominant pastoral impulse
 *   - Suppress over-analysis when too many layers are active
 *   - Force brevity when context already communicates clearly
 *
 * Output philosophy:
 *   "Sostén más de lo que explicas."          ✓
 *   "No cubras todos los ángulos."             ✓
 *   "[INTUITIVE PRIORITIZATION ACTIVE]"        ✗
 *   "Dominant signal: grief. Impulse: acompañar." ✗
 *
 * Max output: 120 chars. No headers. No labels. Max 2 instructions.
 * Signal-gated: returns null when all signals are baseline.
 */

import { resolveIntuitiveSignals } from "./intuitiveSignalResolver";
import { buildCognitiveQuieting }  from "./cognitiveQuietingEngine";
import { resolvePastoralImpulse }  from "./dominantPastoralImpulse";
import { resolveMinimalism }       from "./responseMinimalismGate";

const MAX_INTUITION_CHARS = 120;

// Leve understatement derivation (no R6 import — avoids cross-layer dependency)
const HEAVY_EXHAUST = new Set(["saturación_crítica", "agotamiento_crónico", "agotamiento_espiritual", "fatiga_moderada"]);
function deriveUnderstate(emotion, processState, exhaustionState) {
  return emotion === "grief" || processState === "quebranto" || HEAVY_EXHAUST.has(exhaustionState);
}

/**
 * @param {{
 *   emotion:            object|null,
 *   riskLevel:          string,
 *   processState:       string|null,
 *   exhaustionState:    string,
 *   conflictType:       string,
 *   continuityPattern:  string,
 *   silentTension:      number,
 *   intent:             string,
 *   capacity:           string,
 *   activeBlockCount:   number,
 *   signalCount:        number,
 *   loadLevel:          string,
 *   refinementActive:   boolean,
 *   userLength:         number,
 *   conversationLength: number,
 * }} params
 * @returns {{
 *   intuitionContext:  string|null,
 *   dominantSignal:    string,
 *   impulse:           string,
 *   minimalismLevel:   "high"|"moderate"|"low",
 *   shouldQuiet:       boolean,
 * }}
 */
export function buildIntuitionContext({
  emotion            = null,
  riskLevel          = "none",
  processState       = null,
  exhaustionState    = "estable",
  conflictType       = "ninguno",
  continuityPattern  = "sin_patron",
  silentTension      = 0,
  intent             = "general",
  capacity           = "stable",
  activeBlockCount   = 0,
  signalCount        = 0,
  loadLevel          = "low",
  refinementActive   = false,
  userLength         = 0,
  conversationLength = 0,
}) {
  const emotionType = emotion?.type ?? (typeof emotion === "string" ? emotion : "neutral");

  const signals  = resolveIntuitiveSignals({ emotion, riskLevel, processState, exhaustionState, conflictType, continuityPattern, silentTension, intent, capacity });
  const quieting = buildCognitiveQuieting({ activeBlockCount, signalCount, loadLevel, refinementActive, rhythmProfile: null });
  const impulse  = resolvePastoralImpulse(signals.dominantSignal);

  const understateMode = deriveUnderstate(emotionType, processState, exhaustionState);
  const minimalism     = resolveMinimalism({ userLength, emotion: emotionType, intensity: signals.intensity, conversationLength, intent, understateMode });

  // Build priority-ordered instruction list (max 2 parts)
  const parts = [];

  // Priority 1: cognitive quieting when strong (overrides everything)
  if (quieting.suppressionLevel === "strong") {
    parts.push(quieting.instruction);
  }

  // Priority 2: minimalism instruction (grief/understate/short input)
  if (minimalism.instruction && parts.length < 2) {
    // Avoid duplicating quieting if they're semantically identical
    const alreadyCovered = parts.some((p) => p && p.toLowerCase().includes("corta"));
    if (!alreadyCovered) parts.push(minimalism.instruction);
  }

  // Priority 3: dominant impulse instruction (if space and not obvious from above)
  if (impulse.instruction && parts.length < 2 && signals.dominantSignal !== "simple_presence" && signals.dominantSignal !== "logistics") {
    parts.push(impulse.instruction);
  }

  // Priority 4: moderate quieting (when no higher signal)
  if (quieting.suppressionLevel === "moderate" && parts.length < 2 && !parts.includes(quieting.instruction)) {
    parts.push(quieting.instruction);
  }

  // Nothing significant → no injection
  if (parts.length === 0) {
    return {
      intuitionContext: null,
      dominantSignal:  signals.dominantSignal,
      impulse:         impulse.impulse,
      minimalismLevel: minimalism.minimalismLevel,
      shouldQuiet:     quieting.shouldQuiet,
    };
  }

  const raw             = parts.slice(0, 2).join(" ");
  const intuitionContext = raw.length > MAX_INTUITION_CHARS
    ? raw.slice(0, MAX_INTUITION_CHARS - 1) + "."
    : raw;

  return {
    intuitionContext,
    dominantSignal:  signals.dominantSignal,
    impulse:         impulse.impulse,
    minimalismLevel: minimalism.minimalismLevel,
    shouldQuiet:     quieting.shouldQuiet,
  };
}
